import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Select from "@cloudscape-design/components/select";
import Alert from "@cloudscape-design/components/alert";
import Toggle from "@cloudscape-design/components/toggle";
import { useAuth } from "../contexts/AuthContext";

function ScenarioModal({ mode, scenario, onSubmit, onCancel, visible }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: "", description: "",
    call_direction: { label: t("scenarios.call_direction.inbound"), value: "inbound" },
    system_prompt: "", gateway_targets: "", image_url: "", voice_id: "",
    salesforce_opportunity_id: "",
    visibility: false,
    status: false,
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    if (mode === "edit" && scenario) {
      setFormData({
        name: scenario.name || "",
        description: scenario.description || "",
        call_direction: { label: t(`scenarios.call_direction.${scenario.call_direction}`), value: scenario.call_direction },
        system_prompt: scenario.system_prompt || "",
        gateway_targets: Array.isArray(scenario.gateway_targets) ? scenario.gateway_targets.join(", ") : "",
        image_url: scenario.image_url || "",
        voice_id: scenario.voice_id || "",
        salesforce_opportunity_id: scenario.salesforce_opportunity_id || "",
        visibility: scenario.visibility === "public",
        status: scenario.status === "published",
      });
    } else if (mode === "create") {
      setFormData({
        name: "", description: "",
        call_direction: { label: t("scenarios.call_direction.inbound"), value: "inbound" },
        system_prompt: "", gateway_targets: "", image_url: "", voice_id: "",
        salesforce_opportunity_id: "",
        visibility: false,
        status: false,
      });
    }
    setErrors({});
    setApiError("");
  }, [mode, scenario, visible, t]);

  const callDirectionOptions = [
    { label: t("scenarios.call_direction.inbound"), value: "inbound" },
    { label: t("scenarios.call_direction.outbound"), value: "outbound" },
    { label: t("scenarios.call_direction.both"), value: "both" },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("scenarios.errors.name_required");
    else if (formData.name.length > 100) newErrors.name = t("scenarios.errors.name_too_long");
    if (!formData.description.trim()) newErrors.description = t("scenarios.errors.description_required");
    else if (formData.description.length > 500) newErrors.description = t("scenarios.errors.description_too_long");
    if (!formData.system_prompt.trim()) newErrors.system_prompt = t("scenarios.errors.system_prompt_required");
    else if (formData.system_prompt.length > 5000) newErrors.system_prompt = t("scenarios.errors.system_prompt_too_long");
    if (formData.image_url.trim()) {
      try { new URL(formData.image_url); } catch { newErrors.image_url = t("scenarios.errors.invalid_url"); }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateScenarioId = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setApiError("");
    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        call_direction: formData.call_direction.value,
        system_prompt: formData.system_prompt.trim(),
        gateway_targets: formData.gateway_targets.trim() ? formData.gateway_targets.split(",").map(t => t.trim()).filter(t => t) : [],
        image_url: formData.image_url.trim() || null,
        voice_id: formData.voice_id.trim() || null,
        salesforce_opportunity_id: formData.salesforce_opportunity_id.trim() || null,
        visibility: formData.visibility ? "public" : "private",
        status: formData.status ? "published" : "draft",
      };
      // Only include scenario_id and owner_email for create, not for edit
      if (mode === "create") {
        submitData.scenario_id = generateScenarioId(formData.name.trim());
        submitData.owner_email = user?.email || "";
      }
      await onSubmit(submitData, mode === "edit" ? scenario.scenario_id : null);
    } catch (error) {
      if (error.statusCode === 403) {
        setApiError(t("errors.api.forbidden"));
      } else {
        setApiError(error.message || t("scenarios.errors.submit_failed"));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header={mode === "create" ? t("scenarios.modal.create_title") : t("scenarios.modal.edit_title")}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel} disabled={submitting}>{t("scenarios.modal.cancel")}</Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting}>
              {mode === "create" ? t("scenarios.modal.create_button") : t("scenarios.modal.save_button")}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="large"
    >
      <SpaceBetween size="l">
        {apiError && <Alert type="error">{apiError}</Alert>}
        <FormField label={t("scenarios.form.name")} errorText={errors.name} constraintText={t("scenarios.form.name_constraint")}>
          <Input value={formData.name} onChange={({ detail }) => setFormData({ ...formData, name: detail.value })} placeholder={t("scenarios.form.name_placeholder")} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.description")} errorText={errors.description} constraintText={t("scenarios.form.description_constraint")}>
          <Textarea value={formData.description} onChange={({ detail }) => setFormData({ ...formData, description: detail.value })} placeholder={t("scenarios.form.description_placeholder")} rows={3} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.call_direction")}>
          <Select selectedOption={formData.call_direction} onChange={({ detail }) => setFormData({ ...formData, call_direction: detail.selectedOption })} options={callDirectionOptions} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.system_prompt")} errorText={errors.system_prompt} constraintText={t("scenarios.form.system_prompt_constraint")}>
          <Textarea value={formData.system_prompt} onChange={({ detail }) => setFormData({ ...formData, system_prompt: detail.value })} placeholder={t("scenarios.form.system_prompt_placeholder")} rows={6} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.gateway_targets")} description={t("scenarios.form.gateway_targets_description")}>
          <Input value={formData.gateway_targets} onChange={({ detail }) => setFormData({ ...formData, gateway_targets: detail.value })} placeholder={t("scenarios.form.gateway_targets_placeholder")} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.image_url")} description={t("scenarios.form.image_url_description")} errorText={errors.image_url}>
          <Input value={formData.image_url} onChange={({ detail }) => setFormData({ ...formData, image_url: detail.value })} placeholder={t("scenarios.form.image_url_placeholder")} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.voice_id")} description={<>{t("scenarios.form.voice_id_description")} <a href="https://docs.aws.amazon.com/nova/latest/nova2-userguide/sonic-language-support.html" target="_blank" rel="noopener noreferrer">here</a></>}>
          <Input value={formData.voice_id} onChange={({ detail }) => setFormData({ ...formData, voice_id: detail.value })} placeholder={t("scenarios.form.voice_id_placeholder")} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.salesforce_opportunity_id")} description={t("scenarios.form.salesforce_opportunity_id_description")}>
          <Input value={formData.salesforce_opportunity_id} onChange={({ detail }) => setFormData({ ...formData, salesforce_opportunity_id: detail.value })} placeholder={t("scenarios.form.salesforce_opportunity_id_placeholder")} disabled={submitting} />
        </FormField>
        <FormField label={t("scenarios.form.visibility")} description={t("scenarios.form.visibility_description")}>
          <Toggle checked={formData.visibility} onChange={({ detail }) => setFormData({ ...formData, visibility: detail.checked })} disabled={submitting}>
            {formData.visibility ? t("scenarios.visibility.public") : t("scenarios.visibility.private")}
          </Toggle>
        </FormField>
        <FormField label={t("scenarios.form.status")} description={t("scenarios.form.status_description")}>
          <Toggle checked={formData.status} onChange={({ detail }) => setFormData({ ...formData, status: detail.checked })} disabled={submitting}>
            {formData.status ? t("scenarios.status.published") : t("scenarios.status.draft")}
          </Toggle>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

export default ScenarioModal;
